import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { Observable, fromPromise } from 'apollo-link';
import { createClient } from 'graphql-ws';
import pRetry from 'p-retry';

import { Logger } from '@eluve/logger';

import {
  ApolloClientFactoryOptions,
  defaultApolloClientOptions,
} from './client-options';

const webClientLogger = new Logger('apollo-client.factory');

const JWT_ERRORS = ['JWTExpired', 'Malformed Authorization header'];

export const apolloClientFactory: (
  options: ApolloClientFactoryOptions & {
    logger?: Logger;
  },
) => ApolloClient<NormalizedCacheObject> = (options) => {
  const {
    uri,
    cacheInstance,
    defaultOptions = defaultApolloClientOptions,
    enableSubscriptions = true,
    desiredRole,
    accessTokenProvider,
    logger = webClientLogger,
  } = options;

  const retrieveToken = async (options?: { hardRefresh?: boolean }) => {
    const { jwt, role: sessionRole } = await pRetry(
      async () => accessTokenProvider(options),
      {
        retries: 3,
        minTimeout: 1000,
        maxTimeout: 1000,
      },
    );

    const role = desiredRole ?? sessionRole;

    return {
      jwt,
      role,
    };
  };

  const authLink = setContext(async (_request, { headers }) => {
    const { jwt, role } = await retrieveToken();

    const newHeaders = {
      ...headers,
      Authorization: `Bearer ${jwt}`,
      'x-hasura-role': headers?.['x-hasura-role'] ?? role,
    };

    return {
      headers: newHeaders,
    };
  });

  /**
   * If the request fails due to a JWTExpired error from Hasura, we will attempt to refresh the token
   * and then re-try the request one time
   */
  const retryErrorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      const { operationName } = operation;

      const context = operation.getContext() as { cache?: InMemoryCache };

      if (networkError) {
        // If this is a network error but we've got cached data show that instead of an error
        // state in the UI
        const cachedQueryData = context.cache?.readQuery({
          query: operation.query,
          variables: operation.variables,
        });

        if (cachedQueryData) {
          return Observable.of({ data: cachedQueryData }) as any;
        }
      }

      if (
        graphQLErrors &&
        graphQLErrors.some((e) =>
          JWT_ERRORS.some((errorText) => e.message.includes(errorText)),
        )
      ) {
        logger.warn(
          `Found expired JWT. Attempting to refresh token and retry operation ${operationName}`,
        );

        return fromPromise(
          retrieveToken({
            hardRefresh: true,
          }),
        ).flatMap(({ jwt }) => {
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              Authorization: `Bearer ${jwt}`,
            },
          }));

          return forward(operation);
        });
      }

      if (graphQLErrors) {
        logger.error('Unhandled error in session client factory', {
          operationName,
          graphQLErrors,
        });
      }

      if (networkError) {
        logger.warn('Network error in session client factory', {
          networkError,
        });
      }

      return;
    },
  );

  const retryNetworkLink = new RetryLink({
    attempts: {
      max: 3,
    },
  });

  const httpLink = from([
    authLink,
    retryErrorLink,
    retryNetworkLink,
    createHttpLink({
      uri,
      fetch,
    }),
  ]);

  let finalLink = httpLink;

  // If subscriptions are enabled we need to set up a websocket and then split control
  // over the operations so that subscriptions will be handled by the WS Link
  if (enableSubscriptions) {
    const [protocol, endpoint] = uri.split('//');
    if (!protocol || !endpoint) {
      throw new Error(`Invalid URI provided: ${uri}`);
    }
    const wsUri = protocol.startsWith('https')
      ? `wss://${endpoint}`
      : `ws://${endpoint}`;

    const wsLink = new GraphQLWsLink(
      createClient({
        url: wsUri,
        retryAttempts: 2,
        shouldRetry: () => true,
        connectionParams: async () => {
          const { jwt, role } = await retrieveToken();

          return {
            headers: {
              Authorization: `Bearer ${jwt}`,
              'x-hasura-role': desiredRole ?? role,
            },
          };
        },
      }),
    );

    finalLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink,
    );
  }

  return new ApolloClient({
    cache: cacheInstance,
    link: finalLink,
    defaultOptions,
    connectToDevTools: process.env.NODE_ENV === 'development',
  });
};
